import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Phone } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

/**
 * LegacyGonePage — Rendered for known retired legacy URLs.
 * Sets noindex/nofollow, removes any homepage canonical.
 * Used for Category C (unrelated content) and Category D (WordPress archives).
 * 
 * PRODUCTION BEHAVIOR: Google crawls the page, renders JavaScript,
 * sees noindex, and removes the URL from its index.
 * This follows Google's published JavaScript SEO guidance.
 */
export default function LegacyGonePage() {
  useEffect(() => {
    document.title = 'Page Removed | Evolution Electric';

    // Set noindex, nofollow — critical for de-indexing
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    // Remove any canonical tag — do NOT point to homepage
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.remove();

    // Remove og:url if it inherited homepage
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.remove();

    return () => {
      const tag = document.querySelector('meta[name="robots"][content="noindex, nofollow"]');
      if (tag) tag.remove();
    };
  }, []);

  return (
    <div data-testid="legacy-gone-page">
      <Navbar />
      <main className="min-h-screen flex items-center justify-center px-6 pt-20 bg-white">
        <div className="text-center max-w-lg">
          <p className="font-display text-[8rem] leading-none text-zinc-100 select-none">Gone</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight -mt-8 mb-4" data-testid="gone-title">
            This Page Has Been Removed
          </h1>
          <p className="font-body text-sm text-zinc-500 leading-relaxed mb-8">
            The content previously at this address is no longer available. Evolution Electric is Nashville's trusted licensed electrical contractor.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 bg-yellow-400 text-black font-heading font-bold text-sm uppercase tracking-wider px-6 py-3 border border-black hover:-translate-y-0.5 transition-transform" data-testid="gone-home-btn">
              <Home className="w-4 h-4" /> Visit Evolution Electric
            </Link>
            <a href="tel:+16159615930" className="inline-flex items-center gap-2 bg-black text-white font-heading font-bold text-sm uppercase tracking-wider px-6 py-3 border border-black hover:-translate-y-0.5 transition-transform" data-testid="gone-phone-btn">
              <Phone className="w-4 h-4" /> (615) 961-5930
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
